/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import ReactToPrint from 'react-to-print';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import {
  Button,
  Card,
  Container,
  Row,
  Col,
} from 'reactstrap';
import Swal from 'sweetalert2';
import moment from 'moment';
import XLSX from 'xlsx';

import SimpleHeader from 'components/Headers/SimpleHeader.js';

import { getLoginLogList } from 'apis/admin/userApi';

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        {
          <select
            name="datatable-basic_length"
            aria-controls="datatable-basic"
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        }
      </label>
    </div>
  ),
  paginationTotalRenderer: (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      { from } - { to } / Total { size }
    </span>
  ),
});

const { SearchBar } = Search;

function LoginLogList() {
  const user = useSelector(state => state.user);
  const componentRef = useRef();
  const [logList, setLogList] = useState([]);

  // 로그인 내역 조회
  const getLogList = async () => {
    await getLoginLogList()
      .then(response => {
        const logs = response.data.result;

        if (logs) {
          setLogList(logs);
        }
      })
      .catch(error => {
        console.error('error occured in LoginLogList.js - getLogList() ', error);

        Swal.fire({
          icon: 'error',
          title: '로그인 내역을 불러올 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
        }).then(() => {
          return;
        });
      });
  };

  useEffect(() => {
    if (user) {
      if (user.userData) {
        if (user.userData.isAdmin) {
          getLogList();
        }
      }
    }
  }, [user]);

  // 엑셀 다운로드
  const exportDataToExcel = () => {
    let dataToExport = [...logList];
    dataToExport.reverse();

    dataToExport.forEach((val, idx) => {
      val.success = val.success;
      val.createdAt = moment(val.createdAt).format('YYYY[-]MM[-]DD HH[:]mm');
    });

    let worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const new_workbook = XLSX.utils.book_new();

    const columnsVerKo = [
      '번호',
      '회원 아이디',
      '회원 이름',
      '로그인 시 입력한 아이디',
      '성공 여부',
      '결과 내용',
      '날짜 및 시간',
      '아이피 주소',
      '운영체제',
      '브라우저',
    ];

    columnsVerKo.forEach((val, idx) => {
      const changeCell = XLSX.utils.encode_cell({ c: idx, r:0 });
      worksheet[changeCell].v = val;
    });

    XLSX.utils.book_append_sheet(new_workbook, worksheet, 'SheetJS');
    XLSX.writeFile(new_workbook, `사용자_로그인_내역_${moment(Date.now()).format('YYYY[-]MM[-]DD[_]HH[:]mm')}.xlsx`);
  };

  // 테이블 컬럼
  const columns = [
    {
      dataField: 'id',
      text: '번호',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'userid',
      text: '회원 아이디',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'username',
      text: '회원 이름',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'putid',
      text: '로그인 시 입력한 아이디',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'success',
      text: '성공 여부',
      sort: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) => {
        if (cell === '성공') {
          return (
            <i className="fas fa-check text-success" />
          );
        } else if (cell === '실패') {
          return (
            <i className="fas fa-times text-danger" />
          );
        }
      }
    },
    {
      dataField: 'reason',
      text: '결과 내용',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'createdAt',
      text: '날짜 및 시간',
      sort: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) =>
        cell && moment(cell).format('YYYY[-]MM[-]DD HH[:]mm'),
    },
    {
      dataField: 'ip',
      text: '아이피 주소',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'os',
      text: '운영 체제',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'browser',
      text: '브라우저',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
  ];

  return (
    <>
      <SimpleHeader name="로그인 내역 관리" parentName="회원 설정" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <ToolkitProvider
                data={logList}
                keyField="id"
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <Container fluid>
                      <Row>
                        <Col xs={12} sm={6}>
                          <div
                            id="datatable-basic_filter"
                            className="dataTables_filter"
                          >
                            <label>
                              <span className="text-sm mr-2">
                                <i className="fas fa-search text-muted" />
                              </span>
                              <SearchBar
                                className="form-control-sm"
                                {...props.searchProps}
                                placeholder="아이디, 이름..."
                              />
                            </label>
                          </div>
                        </Col>
                        <Col xs={12} sm={6}>
                          {user && user.userData && user.userData.isAdmin && (
                            <div className="px-2 pb-1 float-right">
                              <>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={exportDataToExcel}
                                >
                                  엑셀 다운로드
                                </Button>
                                <ReactToPrint
                                  trigger={() => (
                                    <Button
                                      color="default"
                                      size="sm"
                                    >
                                      인쇄
                                    </Button>
                                  )}
                                  content={() => componentRef.current}
                                />
                              </>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Container>
                    <BootstrapTable
                      ref={componentRef}
                      {...props.baseProps}
                      bootstrap4={true}
                      pagination={pagination}
                      bordered={false}
                      // selectRow={selectRow}
                      noDataIndication={() => <div className="text-center text-muted">데이터가 없습니다.</div>}
                    />
                  </div>
                )}
              </ToolkitProvider>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default LoginLogList;
